import { defineStore } from "pinia";
import {
  UsuarioPermissoesService,
  IdPermissao,
  IdUsuario,
  IdConta,
  IdInbox,
  PermissaoConfig,
} from "@/services/permissoes/permissoes.service";
import { wrapSafelyAndReturnBool } from "@/utils/tryCatchReportErrorAndReturnBool";
import { useAuthStoreUser } from "@/stores/app/authUser";

/**
 * Chave para armazenar permissões no state
 * Formato: "idConta|idInbox" (idInbox é null para permissões de conta)
 * Exemplo: "conta-123|null" ou "conta-123|inbox-456"
 */
type ContextoChave = string;

/**
 * Interface interna para armazenar permissões de um contexto específico
 */
interface PermissoesContexto {
  // IDs das permissões ativas: ["conta.editar", "inbox.ver_grupos"]
  permissoesIds: IdPermissao[];

  // Timestamp da última atualização
  lastUpdate: number;
}

interface PermissoesConta {
  conta: PermissoesContexto;
  inboxes: {
    [idInbox: string]: PermissoesContexto;
  };
}

/**
 * Interface para armazenar permissões de um usuário
 * Cada contexto (conta/inbox) tem suas próprias permissões
 */
interface PermissoesUsuario {
  [idConta: string]: PermissoesConta;
}

export const usePermissoesStore = defineStore("permissoes", {
  state: () => ({
    initialized: false,

    // Permissões do usuário logado
    // Estrutura: { "conta-123|null": { permissoesIds: [...], permissoesComStatus: [...] }, ... }
    minhasPermissoes: {} as PermissoesUsuario,

    // Permissões de OUTROS usuários (quando admin quer gerenciar)
    // Estrutura: { "user-123": { "conta-123|null": { ... }, ... }, ... }
    permissoesOutrosUsuarios: {} as Record<IdUsuario, PermissoesUsuario>,

    // Todas as permissões disponíveis no sistema (carregadas uma vez)
    todasPermissoes: [] as PermissaoConfig[],

    // Service
    service: new UsuarioPermissoesService(),
  }),

  getters: {
    /**
     * Retorna a chave de contexto formatada
     */
    _construirChaveContexto:
      () =>
      (idConta: IdConta, idInbox?: IdInbox): ContextoChave => {
        return `${idConta}|${idInbox ?? "null"}`;
      },

    /**
     * Obtém as permissões IDs do usuário logado em um contexto específico
     */
    obterMinhasPermissoes:
      (state) =>
      (idConta: IdConta, idInbox?: IdInbox): IdPermissao[] => {
        if (idInbox) {
          return (
            state.minhasPermissoes[idConta]?.inboxes[idInbox]?.permissoesIds ??
            []
          );
        }
        return state.minhasPermissoes[idConta]?.conta?.permissoesIds ?? [];
      },

    /**
     * Verifica se o usuário logado tem uma permissão específica
     */
    temPermissao:
      (state) =>
      (
        idPermissao: IdPermissao,
        idConta: IdConta,
        idInbox?: IdInbox,
      ): boolean => {
        // Root (globalAccess) tem acesso a tudo, espelhando o backend
        // (UsuarioPermissoesService.temPermissao libera globalAccess).
        if (useAuthStoreUser().user?.hasGlobalAccess === true) return true;

        if (idInbox !== null && idInbox !== undefined) {
          return (
            state.minhasPermissoes[idConta]?.inboxes[
              idInbox
            ]?.permissoesIds?.includes(idPermissao) ?? false
          );
        }
        return (
          state.minhasPermissoes[idConta]?.conta?.permissoesIds?.includes(
            idPermissao,
          ) ?? false
        );
      },

  //        temPermissao:
  // () =>
  // (
  //   idPermissao: IdPermissao,
  //   idConta: IdConta,
  //   idInbox?: IdInbox,
  // ): boolean => {
  //   // ✅ Busca permissões do token (com cache)
  //   console.log("entrou no tem permissao")
  //   const token = obterTokenDecodificadoCache();
    
  //   if (!token?.permissoes) return false;

  //   const permissoesConta = token.permissoes[idConta];
    
  //   if (!permissoesConta) return false;

  //   // ✅ Verifica permissão em inbox específico
  //   if (idInbox !== null && idInbox !== undefined) {
  //     return permissoesConta.inboxes[idInbox]?.includes(idPermissao) ?? false;
  //   }
    
  //   // ✅ Verifica permissão em nível de conta
  //   return permissoesConta.conta.includes(idPermissao);
  // },

    /**
     * Obtém permissões IDs de OUTRO usuário (para admin gerenciar)
     */
    obterPermissoesUsuario:
      (state) =>
      (
        idUsuario: IdUsuario,
        idConta: IdConta,
        idInbox?: IdInbox,
      ): IdPermissao[] => {
        if (idInbox) {
          return (
            state.permissoesOutrosUsuarios[idUsuario]?.[idConta]?.inboxes[
              idInbox
            ]?.permissoesIds ?? []
          );
        }
        return (
          state.permissoesOutrosUsuarios[idUsuario]?.[idConta]?.conta
            ?.permissoesIds ?? []
        );
      },
    /**
     * Obtém permissões com status de OUTRO usuário (para admin gerenciar)
     */
    // obterPermissoesUsuarioComStatus: (state) => (idUsuario: IdUsuario, idConta: IdConta, idInbox?: IdInbox): PermissaoComStatus[] => {
    //   const chave = `${idConta}|${idInbox ?? 'null'}`;
    //   return state.permissoesOutrosUsuarios[idUsuario]?.[chave]?.permissoesComStatus ?? [];
    // },

    /**
     * Retorna todas as permissões disponíveis (opcionalmente filtradas por escopo)
     */
    obterTodasPermissoes:
      (state) =>
      (escopo?: "conta" | "inbox"): PermissaoConfig[] => {
        if (!escopo) return state.todasPermissoes;
        return state.todasPermissoes.filter((p) => p.escopo === escopo);
      },
  },

  actions: {
    /**
     * Inicializa o store carregando as permissões do usuário logado
     * Chamado uma vez na inicialização da app
     */
    async init() {
      if (this.initialized) return;

      const authStore = useAuthStoreUser();
      await authStore.init();
      // console.log("authStoreeeeeeeeeeeeeeeeeeee", authStore.isLoggedIn, authStore.user)
      if (!authStore.isLoggedIn || !authStore.user) {
        this.initialized = true;
        // console.log("AI SE ME QUEBRAAAAAAAAAAAAAAAAAAAAA")
        return;
      }

      // Carrega todas as permissões disponíveis
      // this.todasPermissoes = this.service.obterTodasPermissoes();
      // console.log(this.todasPermissoes);

      // Carrega permissões do usuário logado para TODAS as suas contas/inboxes
      await this._carregarPermissoesUsuarioLogado(authStore.user.id);
      // console.log("bababbbbbabbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb", this.minhasPermissoes)

      // Registra listener de WebSocket para atualizar permissões quando mudarem
      this._registrarListenerWebSocket();

      this.initialized = true;
    },

    /**
     * Carrega as permissões do usuário logado para uma conta/inbox específica
     * Armazena AMBOS os tipos: IDs simples + com status
     */
    async _carregarPermissoesUsuarioLogado(idUsuario: IdUsuario) {
        // Uma única chamada já traz todas as contas e inboxes do usuário
        await this._carregarTodasPermissoes(idUsuario, true);
    }, 

    /**
     * Carrega permissões de um contexto específico (conta/inbox)
     * @param idUsuario ID do usuário
     * @param idConta ID da conta
     * @param idInbox ID do inbox (null para permissões de conta)
     * @param ehUsuarioLogado True se está carregando permissões do usuário logado
     */
    async _carregarTodasPermissoes(
      idUsuario: IdUsuario,
      ehUsuarioLogado: boolean = false,
    ) {
      try {
        // Usar endpoint de cache em vez de buscar do banco direto
        // const todasPermissoes = await this.service.obterTodasPermissoes(idUsuario);
        const todasPermissoes =
          await this.service.obterPermissoesDoCache(idUsuario);

        const estruturaFormatada: PermissoesUsuario = {};

        // Organiza na estrutura do store
        for (const [idConta, { conta, inboxes }] of Object.entries(
          todasPermissoes,
        )) {
          estruturaFormatada[idConta] = {
            conta: {
              permissoesIds: conta,
              lastUpdate: Date.now(),
            },
            inboxes: {},
          };

          // Adiciona inboxes
          for (const [idInbox, permissoes] of Object.entries(inboxes)) {
            estruturaFormatada[idConta].inboxes[idInbox] = {
              permissoesIds: permissoes,
              lastUpdate: Date.now(),
            };
          }
        }

        // Armazena no state correto
        if (ehUsuarioLogado) {
          this.minhasPermissoes = estruturaFormatada;
        } else {
          this.permissoesOutrosUsuarios[idUsuario] = estruturaFormatada;
        }
      } catch (error) {
        console.error(
          `❌ [Permissões] Erro ao carregar permissões para ${idUsuario}:`,
          error,
        );
      }
    },

    /**
     * Registra listener de WebSocket para atualizar permissões quando mudarem
     */
    _registrarListenerWebSocket() {
      const authStore = useAuthStoreUser();
      
      const tentarRegistrar = async () => {
        const ws = await authStore.initWebsocket();

        if (!ws) {
          setTimeout(tentarRegistrar, 1000);
          return;
        }

        ws.registerCallback(
          "USUARIO_PERMISSOES_ATUALIZADAS",
          async (payload: any) => {
            console.log("📋 [WebSocket] Permissões atualizadas:", payload);
            const { idUsuario, idConta, idInbox, permissoesIds } = payload;

            // Se é o usuário logado que teve permissão alterada
            if (idUsuario === authStore.user?.id) {
              console.log("🔄 [Permissões] Detectada alteração no usuário atual.");
              
              try {
                // 🔥 CAPTURA ESTADOS ANTERIORES das permissões
                const tinhaPermissaoAtendimentosAntes = this.temPermissao(
                  'inbox.ver_todos_atendimentos',
                  idConta,
                  idInbox
                );
                
                const tinhaPermissaoGruposAntes = this.temPermissao(
                  'inbox.ver_grupos',
                  idConta,
                  idInbox
                );

                // Recarrega as permissões na store local
                await this._carregarTodasPermissoes(idUsuario, true);

                // 🔥 VERIFICA ESTADOS ATUAIS das permissões
                const temPermissaoAtendimentosAgora = this.temPermissao(
                  'inbox.ver_todos_atendimentos',
                  idConta,
                  idInbox
                );
                
                const temPermissaoGruposAgora = this.temPermissao(
                  'inbox.ver_grupos',
                  idConta,
                  idInbox
                );

                // 🆕 VERIFICA SE HOUVE MUDANÇA EM ATENDIMENTOS
                const mudouAtendimentos = tinhaPermissaoAtendimentosAntes !== temPermissaoAtendimentosAgora;
                
                // 🆕 VERIFICA SE HOUVE MUDANÇA EM GRUPOS
                const mudouGrupos = tinhaPermissaoGruposAntes !== temPermissaoGruposAgora;

                // 🔥 SE PELO MENOS UMA DAS PERMISSÕES MUDOU
                if (mudouAtendimentos || mudouGrupos) {
                  console.log("🔄 [Permissões] Detectadas mudanças em permissões críticas:");
                  
                  if (mudouAtendimentos) {
                    console.log(
                      `   - 'inbox.ver_todos_atendimentos': ${tinhaPermissaoAtendimentosAntes ? '✅→❌' : '❌→✅'}`
                    );
                  }
                  
                  if (mudouGrupos) {
                    console.log(
                      `   - 'inbox.ver_grupos': ${tinhaPermissaoGruposAntes ? '✅→❌' : '❌→✅'}`
                    );
                  }
                  const { useChatStore } = await import("@/stores/app/chatStore");
                  const chatStore = useChatStore();

                  // 🆕 RECARREGA CHATS (que já puxa grupos internamente via fetchChats)
                  console.log("🔄 [Permissões] Recarregando chats e grupos...");
                  await chatStore.fetchChats();
                  
                  console.log("✅ [Permissões] Chats e grupos recarregados com sucesso.");
                }

              } catch (error) {
                console.error("❌ [Permissões] Falha ao atualizar permissões após evento de websocket:", error);
              }

            } else {
              // Lógica para outros usuários (admin visualizando, etc)
              void this._carregarTodasPermissoes(idUsuario, false);
            }
          },
        );
      };
      tentarRegistrar();
    },

    /**
     * Atualiza as permissões de um usuário em um contexto específico
     * Substitui TODAS as permissões antigas por novas
     *
     * @param idUsuario ID do usuário
     * @param idConta ID da conta
     * @param idInbox ID do inbox (opcional)
     * @param permissoesIds IDs das novas permissões
     * @returns true se sucesso, false se erro
     */
    async atualizarPermissoes(
      idUsuario: IdUsuario,
      idConta: IdConta,
      idInbox: IdInbox | undefined,
      permissoesIds: IdPermissao[],
    ): Promise<boolean> {
      const result = await wrapSafelyAndReturnBool(async () => {
        const response = await this.service.atualizarPermissoes(
          idUsuario,
          idConta,
          idInbox ?? null,
          permissoesIds,
        );

        // Recarrega as permissões após atualização
        await this._carregarTodasPermissoes(idUsuario);

        console.log("✅ Permissões atualizadas:", response.mensagem);
      });

      return result;
    },

    /**
     * Deleta uma permissão individual do usuário (desmarca checkbox)
     *
     * @param idUsuario ID do usuário
     * @param idPermissao ID da permissão a remover
     * @param idConta ID da conta
     * @param idInbox ID do inbox (opcional)
     * @returns true se sucesso, false se erro
     */
    async deletarPermissao(
      idUsuario: IdUsuario,
      idPermissao: IdPermissao,
      idConta: IdConta,
      idInbox?: IdInbox,
    ): Promise<boolean> {
      const result = await wrapSafelyAndReturnBool(async () => {
        const response = await this.service.deletarPermissao(
          idUsuario,
          idPermissao,
          idConta,
          idInbox,
        );

        // Recarrega as permissões após deleção
        await this._carregarTodasPermissoes(idUsuario);

        console.log("✅ Permissão deletada:", response.mensagem);
      });

      return result;
    },

    /**
     * Verifica se um usuário tem uma permissão específica
     * Faz requisição ao backend para verificação
     *
     * @param idUsuario ID do usuário
     * @param idPermissao ID da permissão
     * @param idConta ID da conta
     * @param idInbox ID do inbox (opcional)
     * @returns true se tem permissão, false caso contrário
     */
    async verificarPermissao(
      idUsuario: IdUsuario,
      idPermissao: IdPermissao,
      idConta: IdConta,
      idInbox?: IdInbox,
    ): Promise<boolean> {
      try {
        const response = await this.service.verificarPermissao(
          idUsuario,
          idPermissao,
          idConta,
          idInbox,
        );
        return response.temPermissao;
      } catch (error) {
        console.error("❌ Erro ao verificar permissão:", error);
        return false;
      }
    },

    /**
     * Carrega as permissões de um usuário específico (para admin gerenciar)
     * Útil quando admin quer ver/editar permissões de outro usuário
     *
     * @param idUsuario ID do usuário
     * @param idConta ID da conta
     * @param idInbox ID do inbox (opcional)
     */
    async carregarPermissoesUsuario(
      idUsuario: IdUsuario,
      idConta: IdConta,
      idInbox?: IdInbox,
    ) {
      await this._carregarTodasPermissoes(idUsuario);
    },

    /**
     * Recarrega as permissões do usuário logado (geralmente após mudança de conta)
     */
    async recarregarMinhasPermissoes() {
      const authStore = useAuthStoreUser();

      if (!authStore.user) return;

      this.minhasPermissoes = {};
      await this._carregarPermissoesUsuarioLogado(authStore.user.id);
    },

    /**
     * Limpa todas as permissões do store (chamado no logout)
     */
    limpar() {
      this.initialized = false;
      this.minhasPermissoes = {};
      this.permissoesOutrosUsuarios = {};
      this.todasPermissoes = [];
    },
  },
});
